import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import {
  applyCartSections,
  getCartSectionIds,
} from '@theme/cart-sections';

/**
 * PDP gift wrap checkbox inside the product form.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox
 *
 * @extends {Component<Refs>}
 */
class GiftWrapPdpOption extends Component {
  /**
   * @returns {boolean}
   */
  isSelected() {
    return Boolean(this.refs.checkbox?.checked);
  }
}

if (!customElements.get('gift-wrap-pdp-option')) {
  customElements.define('gift-wrap-pdp-option', GiftWrapPdpOption);
}

/**
 * @returns {{ enabled?: boolean, variantId?: number, productId?: number, label?: string, propertyKey?: string, propertyValue?: string } | null}
 */
function getGiftWrapConfig() {
  return Theme.giftWrap ?? null;
}

/**
 * @param {object} item
 * @returns {string|null}
 */
export function getParentLineKey(item) {
  return item.parent_relationship?.parent?.key ?? item.parent_relationship?.parent_key ?? null;
}

/**
 * @param {object} item
 * @returns {boolean}
 */
export function isTopLevelCartLine(item) {
  return !getParentLineKey(item);
}

/**
 * @param {object} item
 * @returns {boolean}
 */
export function isGiftWrapFeeLine(item) {
  const cfg = getGiftWrapConfig();
  if (!cfg?.variantId) return false;
  if (!isTopLevelCartLine(item)) return false;
  if (item.variant_id === cfg.variantId) return true;
  if (cfg.productId && item.product_id === cfg.productId) return true;
  return false;
}

/**
 * @param {Array<object>} items
 * @param {string} lineKey
 * @returns {number | null}
 */
export function findLineIndexByKey(items, lineKey) {
  if (!items?.length || !lineKey) return null;

  let lineIndex = 0;
  for (const item of items) {
    lineIndex += 1;
    if (item.key === lineKey) return lineIndex;
  }

  return null;
}

/**
 * @param {Array<object>} items
 * @param {string} lineKey
 * @returns {object | null}
 */
export function findCartItemByKey(items, lineKey) {
  const lineIndex = findLineIndexByKey(items, lineKey);
  if (!lineIndex) return null;
  return items[lineIndex - 1] ?? null;
}

/**
 * @param {object} item
 * @returns {boolean}
 */
export function isLineMarkedForWrap(item) {
  const cfg = getGiftWrapConfig();
  if (!cfg || isGiftWrapFeeLine(item)) return false;

  const properties = item.properties ?? {};
  const value = properties[cfg.propertyKey];

  if (value == null || value === '') return false;

  return String(value) === cfg.propertyValue;
}

/**
 * @param {Array<object>} items
 * @returns {number}
 */
export function countLinesMarkedForWrap(items) {
  if (!items?.length) return 0;
  return items.filter((item) => isLineMarkedForWrap(item)).length;
}

/**
 * @param {Array<object>} items
 * @returns {boolean}
 */
export function anyLineMarkedForWrap(items) {
  return items?.some((item) => isLineMarkedForWrap(item)) ?? false;
}

/**
 * @param {Array<object>} items
 * @returns {{ lineIndex: number, item: object } | null}
 */
export function findGiftWrapFeeLine(items) {
  if (!items?.length) return null;

  let lineIndex = 0;
  for (const item of items) {
    lineIndex += 1;
    if (isGiftWrapFeeLine(item)) {
      return { lineIndex, item };
    }
  }

  return null;
}

/**
 * @param {Record<string, string> | null | undefined} properties
 * @param {boolean} marked
 * @returns {Record<string, string>}
 */
export function mergeGiftWrapProperty(properties, marked) {
  const cfg = getGiftWrapConfig();
  const merged = { ...(properties ?? {}) };

  if (!cfg?.propertyKey) return merged;

  if (marked) {
    merged[cfg.propertyKey] = cfg.propertyValue;
  } else {
    // Explicit empty string clears the property in Cart API updates.
    merged[cfg.propertyKey] = '';
  }

  return merged;
}

/** @type {readonly string[]} */
export const GIFT_WRAP_CART_UPDATE_SOURCES = ['gift-wrap-line-option', 'gift-wrap-fee-sync'];

/**
 * @param {string | undefined} source
 * @returns {boolean}
 */
export function isGiftWrapCartUpdateSource(source) {
  return GIFT_WRAP_CART_UPDATE_SOURCES.includes(source ?? '');
}

/**
 * @returns {string}
 */
function getCartChangeUrl() {
  const url = Theme.routes.cart_change_url;
  return url.endsWith('.js') ? url : `${url}.js`;
}

/**
 * @returns {string}
 */
function getCartUpdateUrl() {
  const url = Theme.routes.cart_update_url;
  return url.endsWith('.js') ? url : `${url}.js`;
}

/**
 * @returns {Promise<object>}
 */
export async function fetchCart() {
  const response = await fetch('/cart.js');
  return response.json();
}

export { getCartSectionIds } from '@theme/cart-sections';

/**
 * @param {object} parsed
 * @param {string} [source]
 */
export function applyGiftWrapCartSections(parsed, source = 'gift-wrap') {
  applyCartSections(parsed, { source, silent: true });
}

/**
 * Ensures a single flat gift wrap fee line matches marked product lines.
 *
 * @param {object} [options]
 * @param {string[]} [options.sections]
 * @returns {Promise<{ changed: boolean, parsed?: object, error?: object }>}
 */
export async function syncGiftWrapFee(options = {}) {
  const cfg = getGiftWrapConfig();
  if (!cfg?.enabled || !cfg.variantId) return { changed: false };

  const cart = await fetchCart();
  const items = cart.items ?? [];
  const markedCount = countLinesMarkedForWrap(items);
  const marked = markedCount > 0;
  const feeLine = findGiftWrapFeeLine(items);
  const sections = options.sections ?? getCartSectionIds();
  const sectionsPayload =
    sections.length > 0
      ? {
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }
      : {};

  if (marked && !feeLine) {
    const body = JSON.stringify({
      updates: {
        [String(cfg.variantId)]: 1,
      },
      ...sectionsPayload,
    });

    const response = await fetch(getCartUpdateUrl(), fetchConfig('json', { body }));
    const parsed = JSON.parse(await response.text());

    if (parsed.errors || parsed.status) {
      console.error('Gift wrap fee add failed:', parsed.message || parsed.description || parsed.errors);
      return { changed: false, error: parsed };
    }

    return { changed: true, parsed };
  }

  if (!marked && feeLine) {
    const body = JSON.stringify({
      line: feeLine.lineIndex,
      quantity: 0,
      ...sectionsPayload,
    });

    const response = await fetch(getCartChangeUrl(), fetchConfig('json', { body }));
    const parsed = JSON.parse(await response.text());

    if (parsed.errors) {
      console.error('Gift wrap fee remove failed:', parsed.errors);
      return { changed: false, error: parsed };
    }

    return { changed: true, parsed };
  }

  return { changed: false, cart };
}

let feeSyncTimeout = /** @type {number | undefined} */ (undefined);

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const cfg = getGiftWrapConfig();
  if (!cfg?.enabled) return;

  // CartAddEvent shares ThemeEvents.cartUpdate but does not extend CartUpdateEvent.
  const source = /** @type {{ detail?: { data?: { source?: string } } }} */ (event).detail?.data?.source;
  if (isGiftWrapCartUpdateSource(source)) return;
  // product-form ATC already awaits syncGiftWrapFee + section morph before open.
  if (source === 'product-form-component') return;

  window.clearTimeout(feeSyncTimeout);
  feeSyncTimeout = window.setTimeout(async () => {
    const result = await syncGiftWrapFee({ sections: getCartSectionIds() });
    if (result.changed && result.parsed?.sections) {
      applyGiftWrapCartSections(result.parsed, 'gift-wrap-fee-sync');
    }
  }, 150);
});
